import { IBusinessDetails } from '@forwardflow/types/onboarding'
import {
   TextInput,
   Grid,
   Stack,
   Group,
   Radio,
   Button,
   Checkbox,
} from '@forwardflow/ui-v2'
import { useState } from 'react'
import { Modal } from 'antd'
import {
   getFilteredVariants,
   getPlaceholderMap,
   renderTemplateWithPlaceholders,
} from '@forwardflow/utils/introductionTemplates'
import { EditIcon } from '@forwardflow/assets/svg/EditIcon'

export const OrganizationDetailsForm = ({
   businessDetails,
   disabled = false,
   onChange,
}: {
   businessDetails?: IBusinessDetails | null
   disabled?: boolean
   onChange?: (values: Partial<IBusinessDetails>) => void
}) => {
   const [errors, setErrors] = useState<
      Partial<Record<keyof IBusinessDetails, string>>
   >({})
   const [isCnamModalOpen, setIsCnamModalOpen] = useState(false)
   const [isIntroModalOpen, setIsIntroModalOpen] = useState(false)
   const [tempCname, setTempCname] = useState('')
   const [confirmCname, setConfirmCname] = useState(false)
   const [tempIntro, setTempIntro] = useState<string | null>(null)

   const filteredVariants = getFilteredVariants(
      Boolean(businessDetails?.hasTeam)
   )
   const placeHolderMap = getPlaceholderMap(businessDetails)

   const validateBusinessOwnerName = (name: string): string => {
      if (!name) return ''
      const nameRegex = /^[a-zA-Z\s.]+$/
      if (!nameRegex.test(name)) {
         return 'Business Owner Name can only consist alphabets, period or space.'
      }
      return ''
   }

   const validateWebsiteUrl = (url: string): string => {
      if (!url) return ''
      if (url.includes(' ')) {
         return 'Enter a valid website URL.'
      }
      // Must have format xxxx.xx (domain.extension)
      const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- ./?%&=]*)?$/
      if (!urlRegex.test(url)) {
         return 'Enter a valid website URL.'
      }
      return ''
   }

   const validateEIN = (ein: string): string => {
      if (!ein) return ''
      const cleanEIN = ein.replace(/[-\s]/g, '')
      if (!/^\d+$/.test(cleanEIN)) {
         return 'Enter a valid 9-digit EIN.'
      }
      if (cleanEIN.length !== 9) {
         return 'Enter a valid 9-digit EIN.'
      }
      return ''
   }

   const validatePostalCode = (zip: string): string => {
      if (!zip) return ''
      if (!/^\d+$/.test(zip)) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      if (zip.length !== 5) {
         return 'ZIP should only be numbers, and 5 digits.'
      }
      return ''
   }

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target

      // Validate on change
      let error = ''
      if (name === 'businessOwnerName') {
         error = validateBusinessOwnerName(value)
      } else if (name === 'websiteUrl') {
         error = validateWebsiteUrl(value)
      } else if (name === 'businessEIIN') {
         error = validateEIN(value)
      } else if (name === 'postalCode') {
         error = validatePostalCode(value)
      }

      setErrors({ ...errors, [name]: error })

      // Update parent component with new values
      if (onChange) {
         onChange({ ...businessDetails, [name]: value })
      }
   }

   const handleTeamChange = (hasTeam: boolean) => {
      if (onChange) {
         onChange({ ...businessDetails, hasTeam })
      }
   }

   return (
      <Stack gap={24} className="p-6">
         <TextInput
            readOnly={disabled}
            value={businessDetails?.legalBusinessName || ''}
            label="Legal business name"
            placeholder="Forward Flow Real Estate LLC"
            name="legalBusinessName"
            onChange={handleChange}
         />

         <Grid>
            <Grid.Col span={6}>
               <TextInput
                  readOnly={disabled}
                  value={businessDetails?.businessOwnerName || ''}
                  label="Business owner"
                  placeholder="John Smith"
                  name="businessOwnerName"
                  onChange={handleChange}
                  error={errors.businessOwnerName}
               />
            </Grid.Col>
            <Grid.Col span={6}>
               <TextInput
                  readOnly={disabled}
                  value={businessDetails?.businessOwnerTitle || ''}
                  label="Your title"
                  placeholder="Agent, Broker, etc."
                  name="businessOwnerTitle"
                  onChange={handleChange}
               />
            </Grid.Col>
         </Grid>

         <Stack>
            <p className="text-white text-sm font-medium">
               Are you part of a Team?
            </p>
            <Group>
               <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                  <Radio
                     name="hasTeam"
                     value="yes"
                     disabled={disabled}
                     checked={businessDetails?.hasTeam || false}
                     onChange={() => handleTeamChange(true)}
                  />
                  <p className="text-white text-sm font-medium">Yes</p>
               </Group>

               <Group className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2">
                  <Radio
                     name="hasTeam"
                     value="no"
                     disabled={disabled}
                     checked={!businessDetails?.hasTeam}
                     onChange={() => handleTeamChange(false)}
                  />
                  <p className="text-white text-sm font-medium">No</p>
               </Group>
            </Group>
         </Stack>

         {businessDetails?.hasTeam && (
            <TextInput
               readOnly={disabled}
               value={businessDetails?.businessTeamName || ''}
               label="Team name"
               placeholder="Forward Team"
               name="businessTeamName"
               onChange={handleChange}
            />
         )}

         <TextInput
            readOnly={disabled}
            value={businessDetails?.businessBrokerageName || ''}
            label="Brokerage name"
            placeholder="FF Brokerage"
            name="businessBrokerageName"
            onChange={handleChange}
         />

         <TextInput
            readOnly={disabled}
            value={businessDetails?.friendlyBusinessName || ''}
            label="Friendly business name"
            placeholder="Forward Flow"
            name="friendlyBusinessName"
            onChange={handleChange}
         />

         <TextInput
            readOnly={disabled}
            value={businessDetails?.websiteUrl || ''}
            label="Website"
            placeholder="https://www.ffrealestate.com"
            name="websiteUrl"
            onChange={handleChange}
            error={errors.websiteUrl}
         />

         <TextInput
            readOnly={disabled}
            value={businessDetails?.businessEIIN || ''}
            label="Business EIN"
            placeholder="12-3456789"
            name="businessEIIN"
            description="We need your EIN for A2P registration - it's required to send verified text messages. You can skip this for now and add it anytime later from Settings → Business Info."
            onChange={handleChange}
            error={errors.businessEIIN}
         />

         <TextInput
            readOnly={disabled}
            value={businessDetails?.street || ''}
            label="Street address"
            placeholder="123 Main Street, Suite 100"
            name="street"
            onChange={handleChange}
         />

         <Grid>
            <Grid.Col span={4}>
               <TextInput
                  readOnly={disabled}
                  value={businessDetails?.city || ''}
                  label="City"
                  placeholder="San Francisco"
                  name="city"
                  onChange={handleChange}
               />
            </Grid.Col>
            <Grid.Col span={4}>
               <TextInput
                  readOnly={disabled}
                  value={businessDetails?.state || ''}
                  label="State"
                  placeholder="CA"
                  name="state"
                  onChange={handleChange}
               />
            </Grid.Col>
            <Grid.Col span={4}>
               <TextInput
                  readOnly={disabled}
                  value={businessDetails?.postalCode || ''}
                  label="ZIP"
                  placeholder="94102"
                  name="postalCode"
                  onChange={handleChange}
                  error={errors.postalCode}
               />
            </Grid.Col>
         </Grid>

         <Stack gap={8}>
            <Group className="items-end justify-between">
               <Stack gap={8} className="flex-1">
                  <p className="text-white/90 text-sm font-medium">CNAM</p>
                  {businessDetails?.businessCname ? (
                     <div className="bg-white/[0.02] border border-white/10 rounded-lg px-4 py-2 h-12 flex items-center flex-1">
                        <p className="text-white text-base">
                           {businessDetails?.businessCname}
                        </p>
                        {!disabled && (
                           <button
                              onClick={() => {
                                 setTempCname(
                                    businessDetails?.businessCname || ''
                                 )
                                 setConfirmCname(
                                    Boolean(businessDetails?.businessCname)
                                 )
                                 setIsCnamModalOpen(true)
                              }}
                              className="ml-auto mb-[2px] p-2 hover:bg-white/10 rounded-md transition-colors"
                           >
                              <EditIcon className="w-5 h-5" />
                           </button>
                        )}
                     </div>
                  ) : (
                     <Button
                        className="w-fit"
                        variant="secondary"
                        onClick={() => setIsCnamModalOpen(true)}
                     >
                        Add CNAM
                     </Button>
                  )}
               </Stack>
            </Group>
         </Stack>

         <Stack gap={8}>
            <Group className="items-end justify-between">
               <Stack gap={8} className="flex-1">
                  <p className="text-white/90 text-sm font-medium">
                     Assistant Introduction Template
                  </p>
                  <div className="bg-white/[0.02] border border-white/10 rounded-lg px-4 py-3 min-h-12 flex items-center">
                     <p className="text-white text-base">
                        {businessDetails?.introductionTemplate
                           ? renderTemplateWithPlaceholders(
                                businessDetails.introductionTemplate,
                                placeHolderMap
                             )
                           : 'Hi, this is'}
                     </p>
                     {!disabled && (
                        <button
                           onClick={() => {
                              setTempIntro(
                                 businessDetails?.introductionTemplate || null
                              )
                              setIsIntroModalOpen(true)
                           }}
                           className="ml-auto mb-[2px] p-2 hover:bg-white/10 rounded-md transition-colors"
                        >
                           <EditIcon className="w-5 h-5" />
                        </button>
                     )}
                  </div>
               </Stack>
            </Group>
         </Stack>

         {/* CNAM Edit Modal */}
         <Modal
            open={isCnamModalOpen}
            onCancel={() => setIsCnamModalOpen(false)}
            footer={null}
            centered
            className="ff-modal"
            width={520}
         >
            <Stack gap={16} className="p-0">
               <div>
                  <h3 className="text-white text-xl font-medium mb-2">
                     Edit CNAM
                  </h3>
                  <p className="text-[#99A1AF] text-sm">
                     CNAM displays Caller ID - bypassing auto-call forward
                     features and directly improving answer rates.
                  </p>
               </div>

               <Stack gap={8}>
                  <p className="text-white/90 text-sm font-medium">
                     Caller ID Name
                  </p>
                  <TextInput
                     value={tempCname}
                     placeholder="eg: FORWARD FLOW"
                     rightSectionWidth={80}
                     rightSection={
                        <span className="text-white/60 font-medium text-xs">
                           {tempCname.length || 0}/15 char
                        </span>
                     }
                     onChange={(e) => {
                        const value = e.target.value
                        if (
                           (/^[a-zA-Z][a-zA-Z0-9\s.,]*$/.test(value) &&
                              value.length <= 15) ||
                           value === ''
                        ) {
                           setTempCname(value)
                        }
                     }}
                  />
                  <p className="text-white/60 text-xs">
                     Only letters, numbers, space, period, comma are allowed.
                     Must start with a letter
                  </p>
               </Stack>

               <Group gap={8}>
                  <Checkbox
                     size="xs"
                     checked={confirmCname}
                     onChange={() => setConfirmCname(!confirmCname)}
                     disabled={!tempCname}
                  />
                  <p className="text-white/60 font-medium text-sm">
                     I confirm this CNAM accurately represents my business/DBA
                  </p>
               </Group>

               <Grid>
                  <Grid.Col span={6}>
                     <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setIsCnamModalOpen(false)}
                     >
                        Cancel
                     </Button>
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <Button
                        className="w-full"
                        disabled={!tempCname || !confirmCname}
                        onClick={() => {
                           if (onChange) {
                              onChange({
                                 ...businessDetails,
                                 businessCname: tempCname,
                              })
                           }
                           setIsCnamModalOpen(false)
                        }}
                     >
                        Update CNAM
                     </Button>
                  </Grid.Col>
               </Grid>
            </Stack>
         </Modal>

         {/* Introduction Template Modal */}
         <Modal
            open={isIntroModalOpen}
            onCancel={() => setIsIntroModalOpen(false)}
            footer={null}
            centered
            className="ff-modal"
            width={600}
         >
            <Stack gap={16} className="">
               <div>
                  <h3 className="text-white text-xl font-medium mb-2">
                     Edit Introduction Template
                  </h3>
                  <p className="text-[#99A1AF] text-sm">
                     Choose how leads hear your intro from the below options
                  </p>
               </div>

               <Stack
                  className="rounded-[16px] border border-[#A684FF]/30 bg-[rgba(166,132,255,0.05)] p-4"
                  gap={12}
               >
                  <span className="inline-block px-2 rounded-[4px] bg-white/10 text-white/60 font-medium text-xs py-0.5 w-fit">
                     Assistant Name
                  </span>
                  <p className="text-[#99A1AF] text-sm font-medium">
                     The assistant's name will be set when you launch your first
                     campaign. We're using a placeholder in previews.
                  </p>
               </Stack>

               <Stack gap={8}>
                  <p className="text-white font-medium text-sm">
                     Select an Introduction template
                  </p>
                  {filteredVariants.map((variant) => (
                     <button
                        key={variant.value}
                        onClick={() => setTempIntro(variant.value)}
                        className="rounded-lg text-left border border-white/[0.1] bg-white/[0.02] hover:bg-white/[0.05] px-4 py-3 transition-colors"
                     >
                        <Group className="flex flex-wrap gap-2">
                           <Radio
                              value={variant.value}
                              readOnly
                              checked={tempIntro === variant.value}
                           />
                           <p>
                              {renderTemplateWithPlaceholders(
                                 variant.value,
                                 placeHolderMap
                              )}
                           </p>
                        </Group>
                     </button>
                  ))}
               </Stack>

               <Grid>
                  <Grid.Col span={6}>
                     <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setIsIntroModalOpen(false)}
                     >
                        Cancel
                     </Button>
                  </Grid.Col>
                  <Grid.Col span={6}>
                     <Button
                        className="w-full"
                        disabled={!tempIntro}
                        onClick={() => {
                           if (onChange && tempIntro) {
                              onChange({
                                 ...businessDetails,
                                 introductionTemplate: tempIntro,
                              })
                           }
                           setIsIntroModalOpen(false)
                        }}
                     >
                        Save Template
                     </Button>
                  </Grid.Col>
               </Grid>
            </Stack>
         </Modal>
      </Stack>
   )
}
